import { Card, CardContent, Divider, Grid, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import React from 'react'
import IndicatorHeaderDialog from '../../IndicatorHeaderDialog'
import PropTypes from 'prop-types'
import IndicatorTable, { InputTable } from '../../IndicatorTable'
import i18n from 'simple-react-i18n'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getYear } from 'date-fns'
import { CardTitle } from 'components/card/NewCard'

import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn'
import ToastrAction from 'toastr/actions/ToastrAction'
import { D101_0, DC_177, DC_178, DC_184, DEFAULT_CONSO_TRANCHE, DEFAULT_SUB_TRANCHE, VP_179, VP_213, VP_214, VP_215, VP_216, VP_219, ZONE_SELECTION_INDEX } from 'rpqs/constants/RpqsConstant'
import CollectivityAction from 'rpqs/actions/CollectivityAction'
import { getIndicatorDialogRow, getZoneDialogRow } from 'rpqs/utils/RPQSUtils'
import { getNumberFormat } from 'utils/NumberUtil'

const ZoneCard = ({
    title='',
    subTitle='',
    rows=[],
}) => {
    const {
        managementEntity,
    } = useSelector(store => ({
        managementEntity: store.CollectivityReducer.managementEntity,
    }), shallowEqual)

    const dispatch = useDispatch()
    const year = getYear(managementEntity.dateEnd)

    return (
        <Card sx={{
            boxShadow: 'none',
            '& > div': { height: 'auto' },
        }}
        >
            <CardTitle
                title={title}
                actions={[
                    {
                        icon: 'post_add',
                        onClick: () => {
                            dispatch(ToastrAction.error(i18n.inDeveloppmentFunctionnality))
                        },
                    },
                ]}
            />
            <CardContent>
                <Stack spacing={2}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>{i18n.priceBands}</TableCell>
                                <TableCell>{`${year - 1}`}</TableCell>
                                <TableCell>{`${year}`}</TableCell>
                                <TableCell>{i18n.unit}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, i) => (
                                <TableRow
                                    key={`${row.tranche} ${title}`}
                                    sx={{
                                        '& td': {
                                            borderBottom: 'none',
                                            pb: i === rows.length-1 ? 1 : 0.5,
                                            pt: i === 0 ? 1 : 0.5,
                                            backgroundColor: row.outlined ? '#ecf1fa' : 'white',
                                        },
                                    }}
                                >
                                    <TableCell>
                                        {row.tranche}
                                    </TableCell>
                                    <TableCell
                                        width={'25%'}
                                    >
                                        {row.prev}
                                    </TableCell>
                                    <TableCell
                                        width={'25%'}
                                    >
                                        <InputTable
                                            value={row.current}
                                            onChange={row.onChange}
                                        />
                                    </TableCell>
                                    <TableCell
                                        width={'10%'}
                                    >
                                        {row.unit}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Card
                        sx={{
                            backgroundColor: '#f3f5f9',
                            boxShadow: 'none',
                        }}
                    >
                        <CardContent>
                            <Stack spacing={2}>
                                <Typography>
                                    {subTitle}
                                </Typography>

                                <Divider/>

                                <Grid container>
                                    <Grid width={'calc(40% + 4px)'}/>
                                    <Grid width={'calc(25% + 2.5px)'}>
                                        {rows[0].prev}
                                    </Grid>
                                    <Grid width={'calc(25% + 2.5px)'}>
                                        <InputTable
                                            readMode
                                            value={rows[0].current}
                                        />
                                    </Grid>
                                    <Grid width={'calc(10% - 9px)'}>
                                        € HT
                                    </Grid>
                                </Grid>
                            </Stack>
                        </CardContent>
                    </Card>
                </Stack>
            </CardContent>
        </Card>
    )
}

ZoneCard.propTypes = {
    title: PropTypes.number,
    subTitle: PropTypes.number,
    rows: PropTypes.arrayOf(PropTypes.shape({
        outlined: PropTypes.bool,
        tranche: PropTypes.string,
        prev: PropTypes.number,
        current: PropTypes.number,
        unit: PropTypes.string,
        onChange: PropTypes.func,
    })),
}

const ScrollZone = ({
    readMode,
}) => {
    const {
        managementEntity,
        zone,
    } = useSelector(store => ({
        managementEntity: store.CollectivityReducer.managementEntity,
        zone: store.CollectivityReducer.zone,
    }), shallowEqual)

    const dispatch = useDispatch()
    const year = getYear(managementEntity.dateEnd)

    // Rows
    const collectivitySubRows = DEFAULT_SUB_TRANCHE.map((t) => ({
        outlined: t.isImportant,
        tranche: t.name,
        prev: zone?.[year-1]?.collectivity?.[t.name],
        current: zone?.[year]?.collectivity?.[t.name],
        unit: '€ HT',
        onChange: v => dispatch(CollectivityAction.updateZone({
            ...zone,
            [year]: { ...zone?.[year],
                collectivity: { ...zone?.[year]?.collectivity,
                    [t.name]: parseInt(v),
                },
            },
        })),
    }))

    const delegationSubRows = DEFAULT_SUB_TRANCHE.map((t) => ({
        outlined: t.isImportant,
        tranche: t.name,
        prev: zone?.[year-1]?.delegation?.[t.name],
        current: zone?.[year]?.delegation?.[t.name],
        unit: '€ HT',
        onChange: v => dispatch(CollectivityAction.updateZone({
            ...zone,
            [year]: { ...zone?.[year],
                delegation: { ...zone?.[year]?.delegation,
                    [t.name]: parseInt(v),
                },
            },
        })),
    }))

    const collectivityConsoRows = DEFAULT_CONSO_TRANCHE.map((t) => ({
        outlined: t.isImportant,
        tranche: t.name,
        prev: zone?.[year-1]?.collectivity?.[t.name],
        current: zone?.[year]?.collectivity?.[t.name],
        unit: '€ HT/m3',
        onChange: v => dispatch(CollectivityAction.updateZone({
            ...zone,
            [year]: { ...zone?.[year],
                collectivity: { ...zone?.[year]?.collectivity,
                    [t.name]: parseInt(v),
                },
            },
        })),
    }))

    const delegationConsoRows = DEFAULT_CONSO_TRANCHE.map((t) => ({
        outlined: t.isImportant,
        tranche: t.name,
        prev: zone?.[year-1]?.delegation?.[t.name],
        current: zone?.[year]?.delegation?.[t.name],
        unit: '€ HT/m3',
        onChange: v => dispatch(CollectivityAction.updateZone({
            ...zone,
            [year]: { ...zone?.[year],
                delegation: { ...zone?.[year]?.delegation,
                    [t.name]: parseInt(v),
                },
            },
        })),
    }))

    const taxesIndicatorRows = [
        getZoneDialogRow({
            codeIndicator: VP_213.code,
            indicatorName: i18n[VP_213.i18n],
            unit: '%',
            currentYear: year,
            zone,
            onChange: v => dispatch(CollectivityAction.updateZone({
                ...zone,
                [year]: { ...zone?.[year],
                    [VP_213.code]: parseInt(v),
                },
            })),
        }),
        getZoneDialogRow({
            codeIndicator: VP_214.code,
            indicatorName: i18n[VP_214.i18n],
            unit: i18n.residents,
            currentIndexZone: 'HT',
            currentYear: year,
            zone,
            onChange: v => dispatch(CollectivityAction.updateZone({
                ...zone,
                [year]: { ...zone?.[year],
                    [VP_214.code]: parseInt(v),
                },
            })),
        }),
        getZoneDialogRow({
            codeIndicator: VP_215.code,
            indicatorName: i18n[VP_215.i18n],
            unit: 'HT',
            currentYear: year,
            zone,
            onChange: v => dispatch(CollectivityAction.updateZone({
                ...zone,
                [year]: { ...zone?.[year],
                    [VP_215.code]: parseInt(v),
                },
            })),
        }),
        getZoneDialogRow({
            codeIndicator: VP_216.code,
            indicatorName: i18n[VP_216.i18n],
            unit: 'HT',
            currentYear: year,
            zone,
            onChange: v => dispatch(CollectivityAction.updateZone({
                ...zone,
                [year]: { ...zone?.[year],
                    [VP_216.code]: parseInt(v),
                },
            })),
        }),
        getZoneDialogRow({
            codeIndicator: VP_219.code,
            indicatorName: i18n[VP_219.i18n],
            unit: 'HT',
            currentYear: year,
            zone,
            onChange: v => dispatch(CollectivityAction.updateZone({
                ...zone,
                [year]: { ...zone?.[year],
                    [VP_219.code]: parseInt(v),
                },
            })),
        }),
        getZoneDialogRow({
            keyLabel: 'totalApplicableTaxesFeesApplicable',
            indicatorName: i18n.totalApplicableTaxesFeesApplicable,
            unit: 'HT',
            readMode,
            currentYear: year,
            zone,
            onChange: v => dispatch(CollectivityAction.updateZone({
                ...zone,
                [year]: { ...zone?.[year],
                    totalApplicableTaxesFeesApplicable: parseInt(v),
                },
            })),
        }),
        getZoneDialogRow({
            codeIndicator: VP_179.code,
            indicatorName: i18n[VP_179.i18n],
            unit: 'TTC',
            readMode,
            currentYear: year,
            zone,
            onChange: v => dispatch(CollectivityAction.updateZone({
                ...zone,
                [year]: { ...zone?.[year],
                    [VP_179.code]: parseInt(v),
                },
            })),
        }),
    ]

    const indicatorRows = [
        getZoneDialogRow({
            keyLabel: 'nbPeopleServed',
            indicatorName: i18n.nbPeopleServed,
            unit: i18n.residents,
            zone,
            currentYear: year,
            onChange: v => dispatch(CollectivityAction.updateZone({
                ...zone,
                [year]: { ...zone?.[year],
                    nbPeopleServed: parseInt(v),
                },
            })),
        }),

        getZoneDialogRow({
            keyLabel: 'serviceAccessfees',
            indicatorName: i18n.serviceAccessfees,
            unit: '€ TTC',
            zone,
            currentYear: year,
            onChange: v => dispatch(CollectivityAction.updateZone({
                ...zone,
                [year]: { ...zone?.[year],
                    serviceAccessfees: parseInt(v),
                },
            })),
        }),

        getZoneDialogRow({
            codeIndicator: DC_177.code,
            indicatorName: i18n[DC_177.i18n],
            unit: '€ TTC',
            zone,
            readMode,
            currentYear: year,
            onChange: v => dispatch(CollectivityAction.updateZone({
                ...zone,
                [year]: { ...zone?.[year],
                    [DC_177.code]: parseInt(v),
                },
            })),
        }),

        getZoneDialogRow({
            codeIndicator: DC_178.code,
            indicatorName: i18n[DC_178.i18n],
            unit: '€ TTC',
            zone,
            readMode,
            currentYear: year,
            onChange: v => dispatch(CollectivityAction.updateZone({
                ...zone,
                [year]: { ...zone?.[year],
                    [DC_178.code]: parseInt(v),
                },
            })),
        }),

        getZoneDialogRow({
            codeIndicator: DC_184.code,
            indicatorName: i18n[DC_184.i18n],
            unit: '€ TTC',
            zone,
            readMode,
            currentYear: year,
            onChange: v => dispatch(CollectivityAction.updateZone({
                ...zone,
                [year]: { ...zone?.[year],
                    [DC_184.code]: parseInt(v),
                },
            })),
        }),
    ]

    return (
        <Grid
            size={'grow'}
            direction={'column'}
            container
            pl={2}
            gap={2}
            overflow={'auto'}
            flexWrap={'nowrap'}
            height={'100%'}
        >
            <IndicatorTable
                year={year}
                indicators={indicatorRows}
            />
            <Grid
                size={'grow'}
                container
                pb={2}
                gap={2}
                direction={'column'}
            >
                <Grid
                    container
                    direction={'row'}
                >
                    <Grid
                        size={'grow'}
                        gap={2}
                        container
                        direction={'column'}
                    >
                        <Typography fontWeight={'bold'} variant='h6'>
                            {i18n.communityShare}
                        </Typography>
                        <ZoneCard
                            rows={collectivitySubRows}
                            title={i18n.subPortionBill}
                            subTitle={`${i18n.annualAmountBillCollectivity} ${year+1}`}
                        />
                        <ZoneCard
                            rows={collectivityConsoRows}
                            title={i18n.consumptionProportionalShare}
                            subTitle={`${i18n.annualAmountBillVariableCollectivity} ${year+1}`}
                        />
                    </Grid>

                    <Grid
                        width={24}
                        justifyContent={'center'}
                        container
                    >
                        <Divider orientation='vertical'/>
                    </Grid>

                    <Grid
                        size={'grow'}
                        gap={2}
                        container
                        direction={'column'}
                    >
                        <Typography fontWeight={'bold'} variant='h6'>
                            {i18n.delegationShare}
                        </Typography>
                        <ZoneCard
                            rows={delegationSubRows}
                            title={i18n.subPortionBill}
                            subTitle={`${i18n.annualAmountBillDelegation} ${year+1}`}
                        />
                        <ZoneCard
                            rows={delegationConsoRows}
                            title={i18n.consumptionProportionalShare}
                            subTitle={`${i18n.annualAmountBillVariableDelegatee} ${year+1}`}
                        />
                    </Grid>
                </Grid>

                <Card sx={{ boxShadow: 'none' }}>
                    <CardTitle
                        title={i18n.taxesAndRoyalties}
                    />
                    <CardContent>
                        <IndicatorTable
                            indicators={taxesIndicatorRows}
                            year={year}
                        />
                    </CardContent>
                </Card>
            </Grid>
        </Grid>

    )
}

ScrollZone.propTypes = {
    readMode: PropTypes.bool,
}

const PonderationZone = () => {
    const {
        rpqs,
        managementEntity,
        zone,
    } = useSelector(store => ({
        rpqs: store.RpqsReducer.rpqs,
        managementEntity: store.CollectivityReducer.managementEntity,
        zone: store.CollectivityReducer.zone,
    }), shallowEqual)

    const dispatch = useDispatch()
    const year = getYear(managementEntity.dateEnd)

    const indicatorRows = [
        getZoneDialogRow({
            keyLabel: 'servicePrice',
            indicatorName: zone.name,
            unit: '€',
            currentYear: year,
            zone,
            onChange: v => dispatch(CollectivityAction.updateZone({
                ...zone,
                [year]: { ...zone?.[year],
                    servicePrice: parseInt(v),
                },
            })),
        }),
        getIndicatorDialogRow({
            entityDialog: managementEntity,
            rpqs,
            codeIndicator: D101_0.code,
            indicatorName: i18n[D101_0.i18n],
            unit: i18n.subscriber,
            resumption: D101_0.name,
        }),
    ]

    return (
        <Grid
            size={'grow'}
            container
            pl={2}
            overflow={'auto'}
            flexWrap={'nowrap'}
            height={'fit-content'}
        >
            <IndicatorTable
                indicators={indicatorRows}
                year={year}
            />
        </Grid>
    )
}

const ZonePanel = ({
    zoneIndex,
    setCurrentZone=() => {},
    readMode = false,
}) => {
    const {
        managementEntity,
        zones,
        zone,
    } = useSelector(store => ({
        managementEntity: store.CollectivityReducer.managementEntity,
        zones: store.CollectivityReducer.zones,
        zone: store.CollectivityReducer.zone,
    }), shallowEqual)

    const dispatch = useDispatch()
    const year = getYear(managementEntity.dateEnd)

    const handleChangeStatus = (status) => {
        const newZones = zones.map((z, i) => i === zoneIndex ? { ...zone, status } : z)
        dispatch(CollectivityAction.updateZones(newZones))
    }

    const currentValue = zone?.[year]?.servicePrice || 0
    const previousValue = zone?.[year-1]?.servicePrice || 0

    return (
        <Grid
            container
            direction={'column'}
            gap={2}
            height={'100%'}
            flexWrap={'nowrap'}
        >
            <IndicatorHeaderDialog
                current={{
                    year,
                    value: `${getNumberFormat(currentValue)} €`,
                }}
                previous={{
                    year: year-1,
                    value: `${getNumberFormat(previousValue)} €`,
                }}
                status={zone?.status}
                offset={44}
                unit={'TTC/m3'}
                text={`${i18n.youEditing} : ${zone.name}`}
                onStatusChange={handleChangeStatus}
            />
            <Divider/>
            <Grid
                container
                direction={'row'}
                flex={1}
                height={'100%'}
                overflow={'auto'}
            >
                <Grid
                    container
                    direction={'column'}
                    pr={2}
                >
                    <IconButton
                        size='large'
                        disabled={zoneIndex === 0}
                        onClick={() => {
                            setCurrentZone(zoneIndex-1)
                            dispatch(CollectivityAction.updateZone(zones[zoneIndex-1]))
                        }}
                    >
                        <ArrowDropUpIcon fontSize='inherit'/>
                    </IconButton>
                    <IconButton
                        size='large'
                        disabled={zoneIndex === zones.length-1}
                        onClick={() => {
                            setCurrentZone(zoneIndex+1)
                            dispatch(CollectivityAction.updateZone(zones[zoneIndex+1]))
                        }}
                    >
                        <ArrowDropDownIcon fontSize='inherit'/>
                    </IconButton>
                    <IconButton
                        size='large'
                        onClick={() => setCurrentZone(ZONE_SELECTION_INDEX)}
                    >
                        <KeyboardReturnIcon fontSize='inherit'/>
                    </IconButton>
                </Grid>
                <Divider orientation='vertical'/>
                {zoneIndex > 0 ?
                    <ScrollZone
                        readMode={readMode}
                    /> :
                    <PonderationZone/>
                }
            </Grid>
        </Grid>
    )
}

ZonePanel.propTypes = {
    zoneIndex: PropTypes.number,
    setCurrentZone: PropTypes.func,
    readMode: PropTypes.bool,
}

export default ZonePanel